import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import { uploadIspPortalProof } from '@/api/isp';

/**
 * The subscriber's proof-of-payment picker.
 *
 * ─── THIS IS A COPY OF A PATTERN PROVEN ON RAY'S OWN PHONE ───
 * Four of my attempts did nothing on his handset. He pointed me at his EasyID
 * form, which opens the Android picker fine, so its markup is the reference
 * and this matches it deliberately, down to the details I had "improved":
 *
 *   - a PLAIN <input>, not a styled one, hidden with an INLINE style;
 *   - an explicit accept LIST, never a wildcard, which makes some Android
 *     pickers behave differently. HEIC is left OUT on purpose: an iPhone
 *     converts to JPEG when the list excludes it, and we cannot store HEIC,
 *     so offering it would only earn a rejection after the upload;
 *   - a normal onClick that calls ref.current.click().
 *
 * 🔴 DO NOT GET CLEVER HERE AGAIN. Not a transparent overlay, not a
 * label-wrapped input, not a visually-hidden control. Each is a better idea on
 * paper and each one shipped a dead button.
 */
interface Props {
   token: string;
   value: string;
   onChange: (url: string) => void;
   /**
    * Called when the tap did NOT open a picker.
    *
    * 🔴 MEASURED, NOT GUESSED FROM THE USER AGENT. Ray, 2026-09-03: *"only in
    * captive."* A phone's wifi sign-in window blocks the picker and reports
    * nothing. But every browser that DOES open one takes focus away from the
    * page to do it — so if the window never blurs and no file arrives, the
    * picker did not open. That is the actual failure, observed, rather than a
    * string match on a browser name that would misfire on Messenger, where
    * uploads work fine.
    */
   onPickerBlocked?: () => void;
}

/** Long enough for a slow phone to raise the picker, short enough to help. */
const PICKER_GRACE_MS = 1500;

const IspProofUpload: React.FC<Props> = ({
   token,
   value,
   onChange,
   onPickerBlocked,
}) => {
   const fileInputRef = useRef<HTMLInputElement>(null);
   const opened = useRef(false);
   const [busy, setBusy] = useState(false);
   const [pct, setPct] = useState(0);
   const [err, setErr] = useState('');

   const openPicker = () => {
      opened.current = false;
      const noteOpened = () => {
         opened.current = true;
      };
      window.addEventListener('blur', noteOpened, { once: true });
      document.addEventListener('visibilitychange', noteOpened, { once: true });

      fileInputRef.current?.click();

      window.setTimeout(() => {
         window.removeEventListener('blur', noteOpened);
         document.removeEventListener('visibilitychange', noteOpened);
         if (!opened.current && !fileInputRef.current?.files?.length) {
            onPickerBlocked?.();
         }
      }, PICKER_GRACE_MS);
   };

   const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      opened.current = true;
      setErr('');
      setPct(0);
      setBusy(true);
      try {
         const { imageUrl } = await uploadIspPortalProof(token, file, setPct);
         onChange(imageUrl);
      } catch (ex) {
         setErr(
            (ex as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not upload that photo. Try again.'
         );
      } finally {
         setBusy(false);
      }
   };

   return (
      <>
         <Drop $filled={!!value} onClick={openPicker}>
            {value ? (
               <Filled>
                  <Thumb src={value} alt="Your payment screenshot" />
                  <FilledText>
                     <FilledTitle>Screenshot attached</FilledTitle>
                     <Replace>Tap to choose a different one</Replace>
                  </FilledText>
               </Filled>
            ) : (
               <Empty>
                  <Icon viewBox="0 0 24 24" aria-hidden="true">
                     <path d="M12 16V4m0 0L8 8m4-4 4 4" />
                     <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
                  </Icon>
                  <EmptyTitle>
                     {busy ? 'Sending your photo' : 'Upload a screenshot'}
                  </EmptyTitle>
                  {/*
                     🔴 A BAR, NOT A WORD. Ray, 2026-09-03: "uploading.." was
                     "not really noticeable". A phone on a cut-off connection
                     can take many seconds here, and a customer who cannot see
                     it moving taps again or gives up.
                  */}
                  {busy ? (
                     <>
                        <Track
                           role="progressbar"
                           aria-valuenow={pct}
                           aria-valuemin={0}
                           aria-valuemax={100}
                           aria-label="Upload progress"
                        >
                           <Fill style={{ width: `${Math.max(6, pct)}%` }} />
                        </Track>
                        <EmptyHint>{pct}%</EmptyHint>
                     </>
                  ) : (
                     <>
                        <EmptyHint>Tap to pick from your gallery</EmptyHint>
                        <Types>JPG, PNG or WebP · up to 5MB</Types>
                     </>
                  )}
               </Empty>
            )}
         </Drop>
         <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: 'none' }}
            onChange={handlePhotoSelect}
            aria-label="Screenshot of your payment"
         />
         {err ? <Err>{err}</Err> : null}
      </>
   );
};

const Drop = styled.div<{ $filled: boolean }>`
   display: block;
   border: 2px dashed ${({ $filled }) => ($filled ? colors.GRAY12 : '#c7d2fe')};
   border-radius: 14px;
   background: ${({ $filled }) => ($filled ? colors.WHITE : '#f8faff')};
   padding: ${({ $filled }) => ($filled ? '10px' : '22px 16px')};
   cursor: pointer;
   transition: border-color 0.15s, background 0.15s;

   &:active {
      border-color: ${colors.PRIMARY};
      background: #eef2ff;
   }
`;
const Empty = styled.div`
   text-align: center;
`;
const Icon = styled.svg`
   width: 30px;
   height: 30px;
   fill: none;
   stroke: ${colors.PRIMARY};
   stroke-width: 1.7;
   stroke-linecap: round;
   stroke-linejoin: round;
   margin-bottom: 8px;
`;
const EmptyTitle = styled.div`
   font-size: 15px;
   font-weight: 800;
   color: ${colors.PRIMARY};
`;
const EmptyHint = styled.div`
   margin-top: 2px;
   font-size: 13px;
   color: ${colors.GRAY5};
`;
const Types = styled.div`
   margin-top: 8px;
   font-size: 11px;
   color: ${colors.GRAY8 ?? colors.GRAY5};
`;
const Track = styled.div`
   height: 8px;
   margin: 12px 8px 6px;
   border-radius: 99px;
   background: #dbe2fb;
   overflow: hidden;
`;
const Fill = styled.div`
   height: 100%;
   border-radius: 99px;
   background: ${colors.PRIMARY};
   transition: width 0.2s ease;
`;
const Filled = styled.div`
   display: flex;
   align-items: center;
   gap: 12px;
`;
const Thumb = styled.img`
   width: 56px;
   height: 56px;
   object-fit: cover;
   border-radius: 10px;
   flex: none;
`;
const FilledText = styled.div``;
const FilledTitle = styled.div`
   font-size: 14px;
   font-weight: 700;
`;
const Replace = styled.div`
   margin-top: 2px;
   font-size: 13px;
   color: ${colors.PRIMARY};
   text-decoration: underline;
`;
const Err = styled.p`
   margin: 8px 0 0;
   font-size: 13px;
   font-weight: 600;
   color: ${colors.RED};
`;

export default IspProofUpload;
